import { z } from "zod";

/**
 * Time range for narrative data
 */
export const timeRangeSchema = z
  .object({
    start: z
      .string()
      .describe('Range start as an ISO-like date string (e.g. "2026-01-01").'),
    end: z
      .string()
      .describe('Range end as an ISO-like date string (e.g. "2026-03-31").'),
    display: z
      .string()
      .describe('Human-readable label for the range (e.g. "Q1 2026").'),
  })
  .describe("The time range the narrative's data covers.");

/**
 * Curator information for human-authored content
 */
export const curatorInfoSchema = z
  .object({
    name: z.string().describe("Curator's display name."),
    role: z
      .string()
      .optional()
      .describe('Curator\'s role (e.g. "Customer Success Manager").'),
    photo: z
      .string()
      .optional()
      .describe(
        'Path to the curator photo (e.g. "/sarah_blackwell_photo.png").',
      ),
    contactEmail: z
      .union([z.string().email(), z.literal("")])
      .optional()
      .describe("Curator's contact email; empty string treated as absent."),
  })
  .describe(
    'Curator info used when a group\'s contentAuthor resolves to "curator".',
  );

/**
 * Font specification — either a plain family name string or an object with
 * family + optional weight. Accepts: "Poppins" or { family: "Poppins", weight: 600 }
 */
const fontNameRegex = /^[A-Za-z0-9\s-]+$/;

export const fontSpecSchema = z
  .union([
    z
      .string()
      .regex(fontNameRegex)
      .describe('Google Font family name (e.g. "Poppins").'),
    z.object({
      family: z
        .string()
        .regex(fontNameRegex)
        .describe('Google Font family name (e.g. "Poppins").'),
      weight: z
        .number()
        .int()
        .min(100)
        .max(900)
        .optional()
        .describe("Font weight, 100-900 (e.g. 600)."),
    }),
  ])
  .describe(
    'Font specification: a Google Font family name string, or { family, weight? } (e.g. "Poppins" or { family: "Poppins", weight: 600 }).',
  );

/**
 * Narrative metadata - customer info, time range, curator details
 */
export const narrativeMetadataSchema = z
  .object({
    customer_id: z.string().describe("Stable customer identifier."),
    customer_name: z
      .string()
      .describe("Customer display name used throughout the narrative."),
    run_id: z
      .string()
      .optional()
      .describe(
        'Pipeline run identifier (e.g. "run_1709..." or "walkthrough_demo").',
      ),
    report_id: z
      .string()
      .describe(
        "Stable narrative identifier; matches the JSON filename without .json and drives the ?report= URL.",
      ),
    time_range: timeRangeSchema,
    timestamp_generated: z
      .string()
      .describe("ISO timestamp of when the narrative was generated."),
    contactEmail: z
      .union([z.string().email(), z.literal("")])
      .optional()
      .describe(
        "Optional email for CSM contact; empty string treated as absent.",
      ),
    curator: curatorInfoSchema
      .optional()
      .describe(
        'Optional curator info for "curator" contentAuthor resolution.',
      ),
    // Open Graph / social sharing metadata
    shareTitle: z
      .string()
      .optional()
      .describe(
        'Title for social sharing (e.g. "Sunshine Valley Smart Insights").',
      ),
    shareDescription: z
      .string()
      .optional()
      .describe("1-2 sentence description for the social share preview."),
    // Custom typography: string or { family, weight? } loaded at runtime
    header_font: fontSpecSchema
      .optional()
      .describe(
        'Google Font for headlines, loaded at runtime (e.g. "Playfair Display" or { family: "Poppins", weight: 600 }).',
      ),
    body_font: fontSpecSchema
      .optional()
      .describe(
        'Google Font for body text, loaded at runtime (e.g. "Inter" or { family: "Inter", weight: 400 }).',
      ),
    access_token: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Access control: when set, the URL must include ?token=<value> to view this narrative.",
      ),
  })
  .describe(
    "Narrative metadata: customer info, time range, sharing, typography, and access control.",
  );

// Export inferred types
export type TimeRange = z.infer<typeof timeRangeSchema>;
export type CuratorInfo = z.infer<typeof curatorInfoSchema>;
export type NarrativeMetadata = z.infer<typeof narrativeMetadataSchema>;
export type FontSpec = z.infer<typeof fontSpecSchema>;
